import { action, computed, makeObservable, observable } from 'mobx';
import { v4 as uuidv4 } from 'uuid';
import * as Sentry from '@sentry/browser';
import logger from '@/utils/logger';

import RootStore from './RootStore';
import type {
  ChatMessage,
  ChatConversation,
  StationContext,
  SuggestedPrompt,
  SocketChatResponse,
  SocketErrorResponse,
  BaseWidget,
  InsightWidgetParams,
} from '@/types/chatbot.types';
import { sendChatMessage as sendSocketMessage } from '@/sockets/aiAssistantSocket';
import {
  mergeInsightContent,
  reassembleTextChunks,
  isValidTextResponse,
  isValidInsightResponse,
  isValidMessageContent,
  createUserMessage,
  createAssistantMessagePlaceholder,
} from '@/utils/chatbot-response.utils';
import {
  loadConversationsFromStorage,
  saveConversationsToStorage,
  limitConversationMessages,
  limitConversations,
  upsertConversation,
  loadAndLimitConversations,
  MAX_CONVERSATIONS_PER_STATION,
} from '@/utils/chatbot-storage.utils';

// Timeout duration in milliseconds (default: 30 seconds)
const SOCKET_RESPONSE_TIMEOUT_MS = 30 * 1000;

export default class ChatbotStore {
  rootStore: typeof RootStore;

  @observable conversations: ChatConversation[] = [];
  @observable conversationsMap: Map<string, ChatConversation> = new Map();
  @observable currentConversationId: string | null = null;
  @observable isLoading = false;
  @observable isStreaming = false;
  @observable isLoadingConversations = false;
  @observable error: string | null = null;
  @observable messageIdCounter = 0;
  // Store chunks by messageId -> sequence -> content
  @observable textChunksMap: Map<string, Map<number, string>> = new Map();
  // Timeout tracking: messageId -> timeout handle
  private timeoutHandles: Map<number, NodeJS.Timeout> = new Map();
  // Store last message details for resending on timeout
  @observable lastPendingMessage: {
    content: string;
    messageType: string;
    insightId?: string;
    messageId: number;
  } | null = null;

  constructor(rootStore: typeof RootStore) {
    makeObservable(this);
    this.rootStore = rootStore;
  }

  @computed get currentConversation(): ChatConversation | null {
    if (!this.currentConversationId) return null;
    return this.conversationsMap.get(this.currentConversationId) || null;
  }

  @computed get currentMessages(): ChatMessage[] {
    return this.currentConversation?.messages || [];
  }

  @computed get stationContext(): StationContext | null {
    const currentStation = this.rootStore.stationStore?.currentStation;
    if (!currentStation) return null;

    return {
      stationId: currentStation.id,
      stationName: currentStation.name,
    };
  }

  @computed get suggestedPrompts(): SuggestedPrompt[] {
    return [
      {
        id: '1',
        label: 'Get a station overview',
        prompt: 'Can you give me an overview of this station current status?',
      },
      {
        id: '2',
        label: 'Prepare for a quarterly meeting',
        prompt: 'Help me prepare for a quarterly customer meeting for this station',
        badge: 'SALESMATE',
      },
      {
        id: '3',
        label: 'Compare performance to peers',
        prompt: 'How does this station performance compare to similar stations?',
      },
    ];
  }

  @action fetchConversations = async (stationId: string): Promise<void> => {
    this.isLoadingConversations = true;
    try {
      const conversations: ChatConversation[] = await this.getUserStationConversations(stationId);

      conversations.forEach(conv => {
        conv.messages.forEach(msg => {
          msg.isFromHistory = true;
          msg.isStreaming = false;
        });
      });

      this.conversations = conversations;
      this.conversationsMap.clear();
      conversations.forEach(conv => {
        this.conversationsMap.set(conv.id, conv);
      });
    } catch (error) {
      logger('Error fetching conversations:', error);
      Sentry.captureException(new Error(`Error fetching conversations: ${error}`));
      this.conversations = [];
      this.conversationsMap.clear();
    } finally {
      this.isLoadingConversations = false;
    }
  };

  /**
   * Fetches conversations for a station
   * Currently uses localStorage as a workaround until backend API is ready
   * TODO: Implement real API call when backend is ready
   * const { data } = await epaxios.get(`/ai-assistant/conversations/station/${stationId}`);
   * return data || [];
   */
  private async getUserStationConversations(stationId: string): Promise<ChatConversation[]> {
    return loadAndLimitConversations(stationId);
  }

  /**
   * Limits messages in a conversation and updates in-memory state
   */
  @action private limitConversationMessagesInMemory(conversationId: string): ChatConversation | null {
    const conversation = this.conversationsMap.get(conversationId);
    if (!conversation) return null;

    const limitedConversation = limitConversationMessages(conversation);

    // Update in-memory state
    this.conversationsMap.set(conversationId, limitedConversation);
    const arrayIndex = this.conversations.findIndex(c => c.id === conversationId);
    if (arrayIndex >= 0) {
      this.conversations[arrayIndex] = limitedConversation;
    }

    return limitedConversation;
  }

  /**
   * Removes conversations from in-memory state that are not in the saved list
   */
  @action private syncInMemoryStateWithStorage(stationId: string, savedConversations: ChatConversation[]): void {
    const savedIds = new Set(savedConversations.map(conv => conv.id));

    // Remove conversations from map that are not in saved list
    const removedIds: string[] = [];
    this.conversationsMap.forEach((conv, id) => {
      if (conv.stationId === stationId && !savedIds.has(id)) {
        removedIds.push(id);
      }
    });
    removedIds.forEach(id => this.conversationsMap.delete(id));

    // Filter conversations array to match saved list
    this.conversations = this.conversations.filter(conv => conv.stationId !== stationId || savedIds.has(conv.id));
  }

  /**
   * Saves a conversation to localStorage
   * Limits messages per conversation and total conversations per station
   */
  @action saveConversation = (conversationId: string): void => {
    const conversation = this.conversationsMap.get(conversationId);
    if (!conversation || !conversation.stationId) {
      logger('Cannot save conversation: conversation not found or missing stationId', conversationId);
      return;
    }

    try {
      // Limit messages in conversation
      const limitedConversation = this.limitConversationMessagesInMemory(conversationId);
      if (!limitedConversation) return;

      // Load existing conversations from storage
      const stationConversations = loadConversationsFromStorage(conversation.stationId);

      // Update or add the conversation
      const updatedConversations = upsertConversation(stationConversations, limitedConversation);

      // Limit total conversations per station
      const conversationsToSave = limitConversations(updatedConversations, MAX_CONVERSATIONS_PER_STATION);

      // Sync in-memory state if conversations were removed
      if (conversationsToSave.length < updatedConversations.length) {
        this.syncInMemoryStateWithStorage(conversation.stationId, conversationsToSave);
      }

      // Save to localStorage
      saveConversationsToStorage(conversation.stationId, conversationsToSave);
    } catch (error) {
      logger('Error saving conversation to localStorage:', error);
      Sentry.captureException(new Error(`Error saving conversation to localStorage: ${error}`));
    }
  };

  @action createNewConversation = () => {
    const newConversation: ChatConversation = {
      id: uuidv4(),
      messages: [],
      stationId: this.stationContext?.stationId,
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    this.conversations.push(newConversation);
    this.conversationsMap.set(newConversation.id, newConversation);
    this.currentConversationId = newConversation.id;
    // Note: Conversation will be saved to localStorage after first message is sent
  };

  @action setCurrentConversation = (conversationId: string | null) => {
    if (conversationId && this.conversationsMap.has(conversationId)) {
      this.currentConversationId = conversationId;
    } else if (conversationId) {
      logger('Conversation not found, creating new one:', conversationId);
      this.createNewConversation();
    } else {
      this.currentConversationId = null;
    }
  };

  @action updateConversation = (conversationId: string, updates: Partial<ChatConversation>) => {
    const conversation = this.conversationsMap.get(conversationId);
    if (conversation) {
      Object.assign(conversation, updates);
      conversation.updatedAt = new Date();
      this.conversationsMap.set(conversationId, conversation);

      const arrayIndex = this.conversations.findIndex(c => c.id === conversationId);
      if (arrayIndex >= 0) {
        this.conversations[arrayIndex] = conversation;
      }
    }
  };

  /**
   * Validates prerequisites for sending a message
   */
  private validateSendMessagePrerequisites(content: string): { isValid: boolean; error?: string } {
    if (!isValidMessageContent(content)) {
      return { isValid: false, error: 'Message content cannot be empty' };
    }

    if (!this.currentConversationId) {
      return { isValid: false, error: 'No active conversation' };
    }

    const stationId = this.stationContext?.stationId;
    if (!stationId) {
      return { isValid: false, error: 'Station ID is required to send message' };
    }

    return { isValid: true };
  }

  /**
   * Adds a user message to the current conversation
   */
  @action private addUserMessageToConversation(content: string): void {
    const conversation = this.conversations.find(conv => conv.id === this.currentConversationId);
    if (!conversation) return;

    const userMessage = createUserMessage(content, this.stationContext?.stationId) as ChatMessage;
    conversation.messages.push(userMessage);
    conversation.updatedAt = new Date();
    this.conversationsMap.set(this.currentConversationId, conversation);

    this.updateConversation(this.currentConversationId, {
      messages: conversation.messages,
      updatedAt: new Date(),
    });

    this.saveConversation(this.currentConversationId);
  }

  /**
   * Creates and adds an assistant message placeholder for streaming response
   */
  @action private addAssistantMessagePlaceholder(messageId: number): ChatMessage {
    const conversation = this.conversations.find(conv => conv.id === this.currentConversationId);
    if (!conversation) {
      throw new Error('Conversation not found when creating assistant message placeholder');
    }

    const assistantMessage = createAssistantMessagePlaceholder(
      messageId,
      this.stationContext?.stationId,
    ) as ChatMessage;

    conversation.messages.push(assistantMessage);
    this.isStreaming = true;
    this.conversationsMap.set(this.currentConversationId, conversation);

    return assistantMessage;
  }

  /**
   * Handles error state when message sending fails
   */
  private handleSendMessageError(error: unknown): void {
    this.error = 'Failed to send message. Please try again.';
    Sentry.captureException(new Error(`Error sending chat message: ${error}`));
    this.isLoading = false;
    this.isStreaming = false;
  }

  @action sendPredefinedMessage = (message: string, messageType = 'text', insightId?: string) => {
    return this.sendMessage(message, messageType, insightId);
  };

  /**
   * Clears timeout for a specific message
   */
  private clearMessageTimeout(messageId: number): void {
    const timeoutHandle = this.timeoutHandles.get(messageId);
    if (timeoutHandle) {
      clearTimeout(timeoutHandle);
      this.timeoutHandles.delete(messageId);
    }
  }

  /**
   * Sets up timeout for a message response
   */
  private setupMessageTimeout(messageId: number, conversationId: string): void {
    // Clear any existing timeout for this message
    this.clearMessageTimeout(messageId);

    const timeoutHandle = setTimeout(() => {
      this.handleMessageTimeout(messageId, conversationId);
    }, SOCKET_RESPONSE_TIMEOUT_MS);

    this.timeoutHandles.set(messageId, timeoutHandle);
  }

  /**
   * Sends a stop signal to the server to cancel processing for a timed-out message
   * TODO: Implement when server supports cancellation/stop signals
   */
  private sendStopSignalToServer(messageId: number, conversationId: string): void {
    // Placeholder for future implementation
    // When server supports cancellation, send a stop signal like:
    // sendSocketMessage(conversationId, messageId, '', 'stop');
    // or implement a dedicated cancelMessage function in aiAssistantSocket.ts
    logger(`Stop signal placeholder for messageId: ${messageId}, conversationId: ${conversationId}`);
  }

  /**
   * Handles timeout when socket response doesn't arrive in time
   */
  @action private handleMessageTimeout(messageId: number, conversationId: string): void {
    logger(`Message timeout for messageId: ${messageId}, conversationId: ${conversationId}`);

    const conversation = this.conversations.find(conv => conv.id === conversationId);
    if (!conversation) {
      logger('Conversation not found for timeout:', conversationId);
      return;
    }

    // Find the assistant message that timed out
    const assistantMessage = conversation.messages.find(
      msg => msg.role === 'assistant' && msg.messageId === messageId.toString(),
    );

    if (assistantMessage) {
      // Mark message as timed out
      assistantMessage.isStreaming = false;
      assistantMessage.hasTimeoutError = true;
      this.isStreaming = false;
      this.isLoading = false;

      // Send stop signal to server (placeholder - not implemented yet)
      this.sendStopSignalToServer(messageId, conversationId);

      // Update conversation
      this.conversationsMap.set(conversationId, conversation);
      this.updateConversation(conversationId, {
        messages: conversation.messages,
        updatedAt: new Date(),
      });
    }

    // Clear timeout handle
    this.clearMessageTimeout(messageId);
  }

  /**
   * Resends the last pending message without adding a new user message
   */
  @action resendLastMessage = (): void => {
    if (!this.lastPendingMessage || !this.currentConversationId) {
      logger('No pending message to resend');
      return;
    }

    // Validate prerequisites
    const validation = this.validateSendMessagePrerequisites(this.lastPendingMessage.content);
    if (!validation.isValid) {
      logger('Cannot resend message:', validation.error);
      return;
    }

    const conversation = this.conversations.find(conv => conv.id === this.currentConversationId);
    if (conversation) {
      // Remove the old failed assistant message with timeout error
      const failedMessageIndex = conversation.messages.findIndex(
        msg => msg.role === 'assistant' && msg.hasTimeoutError,
      );
      if (failedMessageIndex !== -1) {
        conversation.messages.splice(failedMessageIndex, 1);
        this.conversationsMap.set(this.currentConversationId, conversation);
      }
    }

    this.isLoading = true;
    this.error = null;

    try {
      // Generate new message ID and create assistant placeholder
      this.messageIdCounter += 1;
      const messageId = this.messageIdCounter;
      this.addAssistantMessagePlaceholder(messageId);

      // Update last pending message with new messageId
      this.lastPendingMessage = {
        ...this.lastPendingMessage,
        messageId,
      };

      // Set up timeout for this message
      this.setupMessageTimeout(messageId, this.currentConversationId);

      // Send message via socket (without adding new user message)
      sendSocketMessage(
        this.currentConversationId,
        messageId,
        this.lastPendingMessage.content,
        this.lastPendingMessage.messageType,
        this.lastPendingMessage.insightId,
      );
    } catch (error) {
      this.handleSendMessageError(error);
    }
  };

  /**
   * Sends a message to the AI assistant via socket
   */
  @action sendMessage = async (content: string, messageType = 'text', insightId?: string) => {
    // Validate prerequisites
    const validation = this.validateSendMessagePrerequisites(content);
    if (!validation.isValid) {
      logger('Cannot send message:', validation.error);
      return;
    }

    this.isLoading = true;
    this.error = null;

    const conversationId = this.currentConversationId;
    if (!conversationId) {
      logger('Cannot send message: no current conversation');
      return;
    }

    try {
      // Add user message to conversation
      this.addUserMessageToConversation(content);

      // Generate message ID and create assistant placeholder
      this.messageIdCounter += 1;
      const messageId = this.messageIdCounter;
      this.addAssistantMessagePlaceholder(messageId);

      // Store message details for potential resend
      this.lastPendingMessage = {
        content: content.trim(),
        messageType,
        insightId,
        messageId,
      };

      // Set up timeout for this message
      this.setupMessageTimeout(messageId, conversationId);

      // Send message via socket
      sendSocketMessage(conversationId, messageId, content.trim(), messageType, insightId);
    } catch (error) {
      this.handleSendMessageError(error);
    }
  };

  /**
   * Finds or creates an assistant message for the given conversation and messageId
   */
  private findOrCreateAssistantMessage(
    conversation: ChatConversation,
    messageId: string,
    isComplete: boolean,
  ): ChatMessage {
    // Try to find by messageId first
    let assistantMessage = conversation.messages.find(msg => msg.role === 'assistant' && msg.messageId === messageId);

    // Fallback: find streaming message without messageId
    if (!assistantMessage) {
      assistantMessage = conversation.messages.find(
        msg => msg.role === 'assistant' && msg.isStreaming && !msg.messageId,
      );
    }

    // Create new message if not found
    if (!assistantMessage) {
      assistantMessage = {
        id: uuidv4(),
        role: 'assistant',
        content: '',
        timestamp: new Date(),
        stationId: this.stationContext?.stationId,
        isStreaming: !isComplete,
        messageId: messageId,
        isFromHistory: false,
      };
      conversation.messages.push(assistantMessage);
    } else {
      // Update messageId if it wasn't set
      if (!assistantMessage.messageId) {
        assistantMessage.messageId = messageId;
      }
    }

    return assistantMessage;
  }

  /**
   * Updates or creates a widget in the assistant message
   */
  private updateMessageWidget(
    assistantMessage: ChatMessage,
    widgetType: BaseWidget['type'],
    widgetParams: BaseWidget['params'],
  ): void {
    const existingWidgetIndex = assistantMessage.widgets?.findIndex(w => w.type === widgetType) ?? -1;
    const newWidget: BaseWidget = {
      type: widgetType,
      params: widgetParams,
    };

    if (existingWidgetIndex >= 0 && assistantMessage.widgets) {
      assistantMessage.widgets[existingWidgetIndex] = newWidget;
    } else {
      assistantMessage.widgets = [...(assistantMessage.widgets || []), newWidget];
    }
  }

  /**
   * Finalizes a message when streaming is complete
   */
  private finalizeMessage(conversationId: string, conversation: ChatConversation, assistantMessage: ChatMessage): void {
    assistantMessage.isStreaming = false;
    assistantMessage.isFromHistory = true;
    this.isStreaming = false;

    if (conversationId === this.currentConversationId) {
      this.isLoading = false;
      this.updateConversation(conversationId, {
        messages: conversation.messages,
        updatedAt: new Date(),
      });
    }

    this.saveConversation(conversationId);
  }

  /**
   * Handles chunked text responses
   */
  @action private handleTextResponse(
    conversation: ChatConversation,
    conversationId: string,
    messageId: string,
    content: string,
    sequence: number,
    isComplete: boolean,
    assistantMessage: ChatMessage,
  ): void {
    // Clear timeout since we received a response
    const numericMessageId = parseInt(messageId, 10);
    if (!isNaN(numericMessageId)) {
      this.clearMessageTimeout(numericMessageId);
    }

    // Clear timeout error if it was set (response arrived before timeout or after stop flag)
    if (assistantMessage.hasTimeoutError) {
      assistantMessage.hasTimeoutError = false;
    }

    // Initialize chunks map for this message if it doesn't exist
    if (!this.textChunksMap.has(messageId)) {
      this.textChunksMap.set(messageId, new Map());
    }

    const chunksMap = this.textChunksMap.get(messageId);
    if (!chunksMap) {
      logger('Chunks map not found for messageId:', messageId);
      return;
    }
    chunksMap.set(sequence, content);

    // Reassemble text in order using utility function
    const reassembledText = reassembleTextChunks(chunksMap);

    // Update message content with reassembled text
    assistantMessage.content = reassembledText;
    assistantMessage.isStreaming = !isComplete;

    // Update Text widget
    this.updateMessageWidget(assistantMessage, 'Text', {
      content: reassembledText,
    });

    // Clean up chunks map and finalize if complete
    if (isComplete) {
      this.textChunksMap.delete(messageId);
      this.finalizeMessage(conversationId, conversation, assistantMessage);
    } else {
      this.isStreaming = true;
    }

    this.conversationsMap.set(conversationId, conversation);
  }

  /**
   * Handles insight/structured responses with incremental JSON chunks
   */
  @action private handleInsightResponse(
    conversation: ChatConversation,
    conversationId: string,
    messageId: string,
    content: string | object,
    isComplete: boolean,
    assistantMessage: ChatMessage,
  ): void {
    // Clear timeout since we received a response
    const numericMessageId = parseInt(messageId, 10);
    if (!isNaN(numericMessageId)) {
      this.clearMessageTimeout(numericMessageId);
    }

    // Clear timeout error if it was set (response arrived before timeout or after stop flag)
    if (assistantMessage.hasTimeoutError) {
      assistantMessage.hasTimeoutError = false;
    }

    // Get existing content from the message if it exists
    const existingWidget = assistantMessage.widgets?.find(w => w.type === 'Insight');
    const existingContent = (existingWidget?.params as InsightWidgetParams)?.content;

    // Merge incremental JSON content using utility function
    const mergedContent = mergeInsightContent(existingContent, content);

    // Update Insight widget
    this.updateMessageWidget(assistantMessage, 'Insight', {
      content: mergedContent,
    });

    // Finalize or continue streaming
    if (isComplete) {
      this.finalizeMessage(conversationId, conversation, assistantMessage);
    } else {
      assistantMessage.isStreaming = true;
      this.isStreaming = true;
    }

    this.conversationsMap.set(conversationId, conversation);
  }

  /**
   * Main handler for socket responses - routes to appropriate handler based on response type
   */
  @action handleSocketResponse = (response: SocketChatResponse | SocketErrorResponse) => {
    // Handle error responses
    if (response.eventName === 'chatError') {
      this.handleError(response as SocketErrorResponse);
      return;
    }

    const chatResponse = response as SocketChatResponse;
    const { conversationId, messageId, type, content, isComplete, sequence } = chatResponse.payload;

    // Find conversation
    const conversation = this.conversations.find(conv => conv.id === conversationId);
    if (!conversation) {
      logger('Conversation not found for response:', conversationId);
      return;
    }

    // Find or create assistant message
    const assistantMessage = this.findOrCreateAssistantMessage(conversation, messageId, isComplete ?? false);

    // Route to appropriate handler based on response type
    if (isValidTextResponse(type, content, sequence)) {
      if (sequence === undefined) {
        logger('Sequence is undefined for text response:', { messageId, type });
        return;
      }
      this.handleTextResponse(
        conversation,
        conversationId,
        messageId,
        content as string,
        sequence,
        isComplete ?? false,
        assistantMessage,
      );
    } else if (isValidInsightResponse(type, content)) {
      this.handleInsightResponse(
        conversation,
        conversationId,
        messageId,
        content,
        isComplete ?? false,
        assistantMessage,
      );
    } else {
      logger('Unknown or invalid response type:', { type, hasContent: !!content, hasSequence: sequence !== undefined });
    }

    conversation.updatedAt = new Date();
  };

  @action handleError = (errorResponse: SocketErrorResponse) => {
    const { conversationId, error } = errorResponse.payload;

    const conversation = this.conversations.find(conv => conv.id === conversationId);
    if (conversation) {
      const placeholderIndex = conversation.messages.findIndex(
        msg => msg.role === 'assistant' && msg.isStreaming && !msg.messageId,
      );
      if (placeholderIndex !== -1) {
        conversation.messages.splice(placeholderIndex, 1);
      }
    }

    // Clear any pending timeouts
    this.timeoutHandles.forEach(timeout => {
      clearTimeout(timeout);
    });
    this.timeoutHandles.clear();

    if (conversationId === this.currentConversationId) {
      this.error = error;
    }

    this.isLoading = false;
    this.isStreaming = false;
  };
}
